import { __extends } from "tslib";
import { EventDispatcher } from '@awayjs/core';
import { PassEvent } from '@awayjs/renderer';
/**
 * PassBase provides an abstract base class for material shader passes. A material pass constitutes at least
 * a render call per required renderable.
 */
var PassBase = /** @class */ (function (_super) {
    __extends(PassBase, _super);
    /**
     * Creates a new PassBase object.
     */
    function PassBase(renderMaterial, renderElements) {
        var _this = _super.call(this) || this;
        _this._preserveAlpha = true;
        _this._forceSeparateMVP = false;
        _this._renderMaterial = renderMaterial;
        _this._renderElements = renderElements;
        _this._stage = renderElements.stage;
        return _this;
    }
    Object.defineProperty(PassBase.prototype, "shader", {
        get: function () {
            return this._shader;
        },
        enumerable: false,
        configurable: true
    });
    Object.defineProperty(PassBase.prototype, "preserveAlpha", {
        /**
         * Indicates whether the output alpha value should remain unchanged compared to the material's original alpha.
         */
        get: function () {
            return this._preserveAlpha;
        },
        set: function (value) {
            if (this._preserveAlpha == value)
                return;
            this._preserveAlpha = value;
            this.invalidate();
        },
        enumerable: false,
        configurable: true
    });
    Object.defineProperty(PassBase.prototype, "forceSeparateMVP", {
        /**
         * Indicates whether the screen projection should be calculated by forcing a separate scene matrix and
         * view-projection matrix. This is used to prevent rounding errors when using multiple passes with different
         * projection code.
         */
        get: function () {
            return this._forceSeparateMVP;
        },
        set: function (value) {
            if (this._forceSeparateMVP == value)
                return;
            this._forceSeparateMVP = value;
            this.invalidate();
        },
        enumerable: false,
        configurable: true
    });
    /**
     * Marks the shader program as invalid, so it will be recompiled before the next render.
     */
    PassBase.prototype.invalidate = function () {
        this._shader.invalidateProgram();
        this.dispatchEvent(new PassEvent(PassEvent.INVALIDATE, this));
    };
    /**
     * Cleans up any resources used by the current object.
     * @param deep Indicates whether other resources should be cleaned up, that could potentially be shared across different instances.
     */
    PassBase.prototype.dispose = function () {
        this._renderMaterial = null;
        this._renderElements = null;
        this._stage = null;
        if (this._shader) {
            this._shader.dispose();
            this._shader = null;
        }
    };
    /**
     * Renders the current pass. Before calling pass, activatePass needs to be called with the same index.
     * @param pass The pass used to render the renderable.
     * @param renderable The IRenderable object to draw.
     * @param stage The Stage object used for rendering.
     * @param entityCollector The EntityCollector object that contains the visible scene data.
     * @param viewProjection The view-projection matrix used to project to the screen. This is not the same as
     * camera.viewProjection as it includes the scaling factors when rendering to textures.
     *
     * @internal
     */
    PassBase.prototype._setRenderState = function (renderState) {
        this._shader._setRenderState(renderState);
    };
    /**
     * Sets the render state for the pass that is independent of the rendered object. This needs to be called before
     * calling pass. Before activating a pass, the previously used pass needs to be deactivated.
     * @param stage The Stage object which is currently used for rendering.
     * @param camera The camera from which the scene is viewed.
     * @private
     */
    PassBase.prototype._activate = function () {
        this._shader._activate();
    };
    /**
     * Clears the render state for the pass. This needs to be called before activating another pass.
     * @param stage The Stage used for rendering
     *
     * @private
     */
    PassBase.prototype._deactivate = function () {
        this._shader._deactivate();
    };
    PassBase.prototype._includeDependencies = function (shader) {
        shader.alphaThreshold = this._renderMaterial.material.alphaThreshold;
        shader.useImageRect = this._renderMaterial.material.imageRect;
        shader.usesCurves = this._renderMaterial.material.curves;
        shader.useBothSides = this._renderMaterial.material.bothSides;
        shader.usesUVTransform = this._renderMaterial.material.animateUVs;
        shader.usesColorTransform = this._renderMaterial.material.useColorTransform;
        if (this._forceSeparateMVP)
            this._shader.globalPosDependencies++;
    };
    PassBase.prototype._initConstantData = function () {
    };
    PassBase.prototype._getVertexCode = function (registerCache, sharedRegisters) {
        return '';
    };
    PassBase.prototype._getFragmentCode = function (registerCache, sharedRegisters) {
        return '';
    };
    PassBase.prototype._getPostAnimationFragmentCode = function (registerCache, sharedRegisters) {
        return '';
    };
    PassBase.prototype._getNormalVertexCode = function (registerCache, sharedRegisters) {
        return '';
    };
    PassBase.prototype._getNormalFragmentCode = function (registerCache, sharedRegisters) {
        return '';
    };
    return PassBase;
}(EventDispatcher));
export { PassBase };
